import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'

interface State {
  error: Error | null
}

/** Catches render errors; shows a calm recovery screen, never a stack trace. */
export class ErrorBoundary extends Component<{ children: ReactNode; fallback?: ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      return (
        this.props.fallback ?? (
          <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center" role="alert">
            <div className="flex size-11 items-center justify-center rounded-xl bg-danger-soft text-danger">
              <AlertTriangle className="size-5" />
            </div>
            <h1 className="text-base font-semibold">Algo correu mal nesta página</h1>
            <p className="max-w-sm text-sm text-fg-muted">Pode recarregar a página — os seus dados estão guardados.</p>
            <div className="mt-2 flex gap-2">
              <button className="h-8 rounded-md border border-border bg-surface px-3 text-sm hover:bg-hover" onClick={() => this.setState({ error: null })}>
                Tentar novamente
              </button>
              <button className="h-8 rounded-md bg-fg px-3 text-sm text-bg" onClick={() => window.location.reload()}>
                Recarregar
              </button>
            </div>
          </div>
        )
      )
    }
    return this.props.children
  }
}
